<template>
  <div>
    <TabsNavigation v-if="tabsWithBadge.length > 0" :tabs="tabsWithBadge" class="my-6" />

    <ClaimsIndex
      v-if="planSaleFriendlyId && currentTab === 'historical-claims'"
      class="mt-6"
      :planSaleFriendlyId="planSaleFriendlyId"
      :excludeClaimId="claim.friendlyId"
    />

    <Quotations
      v-if="currentTab === 'quotations'"
      :quotations="claimQuotations"
      class="mt-6"
      @updated="$emit('updated')"
    />

    <Invoices v-if="currentTab === 'invoices'" :invoices="claimInvoices" class="mt-6" @updated="$emit('updated')" />

    <VoucherInformation v-if="currentTab === 'voucher' && claim.voucher" :voucher="claim.voucher" class="mt-6" />

    <ClaimCoverageInformation v-if="currentTab === 'coverages'" :claim="claim" class="mt-6" />

    <div v-if="currentTab === 'irreparability-certificate' && hasIrreparabilityCertificate" class="mt-6 space-y-4">
      <ClaimIrreparabilityInformation
        v-for="unfixeableClaimProduct in unfixeableClaimProducts"
        :key="unfixeableClaimProduct.id"
        :product="unfixeableClaimProduct"
      />
    </div>

    <ClaimDeductibleInformation
      v-if="currentTab === 'deductible' && claim.claimDeductible"
      :claim-deductible="claim.claimDeductible"
      class="mt-6"
    />

    <FinalSettlementInformation
      v-if="currentTab === 'final-settlement' && claim.finalSettlement"
      :final-settlement="claim.finalSettlement"
      class="mt-6"
    />
  </div>
</template>

<script>
import { TabsNavigation, VoucherInformation } from "@estaly/ui";
import ClaimDeductibleInformation from "@/components/claim/details/information/ClaimDeductibleInformation.vue";
import FinalSettlementInformation from "@/components/claim/details/information/FinalSettlementInformation.vue";
import ClaimsIndex from "@/components/claim/details/information/ClaimsIndex.vue";
import Invoices from "@/components/claim/details/invoice/Invoices.vue";
import Quotations from "@/components/claim/details/quotation/Quotations.vue";
import ClaimCoverageInformation from "@/components/claim/details/information/ClaimCoverageInformation.vue";
import ClaimIrreparabilityInformation from "@/components/claim/details/information/ClaimIrreparabilityInformation.vue";

export default {
  components: {
    TabsNavigation,
    VoucherInformation,
    ClaimDeductibleInformation,
    FinalSettlementInformation,
    ClaimsIndex,
    Invoices,
    Quotations,
    ClaimCoverageInformation,
    ClaimIrreparabilityInformation,
  },

  props: {
    claim: {
      type: Object,
      required: true,
    },
    claimQuotations: {
      type: Array,
      default: () => [],
    },
    claimInvoices: {
      type: Array,
      default: () => [],
    },
  },

  emits: ["updated"],

  watch: {
    currentTab: {
      immediate: true,
      handler(resolvedTab) {
        if (resolvedTab && resolvedTab !== this.$route.query.tab) {
          this.$router.replace({ query: { ...this.$route.query, tab: resolvedTab } });
        }
      },
    },
  },

  computed: {
    planSaleFriendlyId() {
      return this.claim?.planSale?.friendlyId;
    },

    unfixeableClaimProducts() {
      return this.claim?.unfixeableClaimProducts || [];
    },

    hasIrreparabilityCertificate() {
      return this.unfixeableClaimProducts.some((product) => product.irreparabilityCertificateUrl);
    },

    currentTab() {
      const requestedTab = this.$route.query.tab;
      const availableTabs = this.tabsWithBadge.map((tab) => tab.name);

      if (availableTabs.length === 0) return null;
      if (requestedTab && availableTabs.includes(requestedTab)) return requestedTab;
      return availableTabs[0];
    },

    tabsWithBadge() {
      const availableTabs = [
        {
          name: "historical-claims",
          displayName: "Sinistres précédents",
          query: { tab: "historical-claims" },
        },
      ];

      availableTabs.push({
        name: "coverages",
        displayName: "Prise en charge",
        query: { tab: "coverages" },
      });

      if (this.hasIrreparabilityCertificate) {
        availableTabs.push({
          name: "irreparability-certificate",
          displayName: "Attestation d'irréparabilité",
          query: { tab: "irreparability-certificate" },
        });
      }

      if (this.claimQuotations.length > 0) {
        const tab = {
          name: "quotations",
          displayName: "Devis",
          query: { tab: "quotations" },
        };
        if (this.claim.quotation?.approvalStatus === "to_be_approved") {
          tab.badge = {
            text: "!",
            class: "bg-yellow-100 text-yellow-800",
          };
        }

        availableTabs.push(tab);
      }

      if (this.claimInvoices.length > 0) {
        const tab = {
          name: "invoices",
          displayName: "Facture",
          query: { tab: "invoices" },
        };

        if (this.claim.invoice?.approvalStatus === "to_be_approved") {
          tab.badge = {
            text: "!",
            class: "bg-yellow-100 text-yellow-800",
          };
        }

        availableTabs.push(tab);
      }

      if (this.claim?.voucher) {
        availableTabs.push({
          name: "voucher",
          displayName: "Avoirs",
          query: { tab: "voucher" },
        });
      }

      if (this.claim?.claimDeductible) {
        availableTabs.push({
          name: "deductible",
          displayName: "Franchise",
          query: { tab: "deductible" },
        });
      }

      if (this.claim?.finalSettlement) {
        availableTabs.push({
          name: "final-settlement",
          displayName: "Solde année en cours",
          query: { tab: "final-settlement" },
        });
      }

      return availableTabs;
    },
  },
};
</script>
